import React from 'react';
import { formatTimeframe, formatDuration } from '../lib/formatting';

/**
 * Event data interface for guest view
 * Privacy-safe: Only includes information guests should see
 */
export interface EventDetailsData {
  title: string;
  organizerName: string;
  timeframe: string;
  duration: string;
  customDuration?: { hours: number; minutes: number };
  meetingFormat?: 'in_person' | 'virtual' | 'undecided';
  location?: { name?: string; address?: string };
  status: string;
}

/**
 * EventDetails component props
 */
export interface EventDetailsProps {
  event: EventDetailsData;
  participantName: string;
  onContinue: () => void;
}

/**
 * EventDetails Component
 *
 * Guest-facing hangout details view after clicking magic link.
 * Shows event information in a privacy-safe manner without exposing
 * other participants or sensitive data.
 *
 * Design: Clean, scannable information hierarchy with prominent CTA
 * Story: 4.3 - Guest Hangout Details View
 *
 * Privacy Features:
 * - Only shows organizer name (no contact info)
 * - No participant list or count
 * - No other participants' availability
 *
 * @param event - Event data from token validation
 * @param participantName - Guest's name for personalization
 * @param onContinue - Callback to transition to availability picker
 */
export const EventDetails: React.FC<EventDetailsProps> = ({
  event,
  participantName: _participantName,
  onContinue,
}) => {
  // Format meeting format text
  const getMeetingFormatText = () => {
    if (!event.meetingFormat) return null;

    if (event.meetingFormat === 'virtual') {
      return 'Virtual';
    }

    if (event.meetingFormat === 'undecided') {
      return "We'll decide later";
    }

    // in_person
    return (
      <>
        <div className="font-medium text-landing-near-black">In person</div>
        {event.location?.name && (
          <div className="text-landing-near-black">{event.location.name}</div>
        )}
        {event.location?.address && (
          <div className="text-gray-600 text-base">{event.location.address}</div>
        )}
      </>
    );
  };

  return (
    <main className="relative flex w-full flex-col items-center justify-center min-h-screen overflow-hidden bg-landing-dark font-landing">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          alt="Background of social gathering"
          className="h-full w-full object-cover object-center"
          src="https://lh3.googleusercontent.com/aida-public/AB6AXuDpr9oVlfgaoqE_IO5khX_xZ8xarwkTaYH3kmcrO4nUFWwFAOh77leF8mopEvPHbMGazdwvtUM72Edr4Kb1WBPy5YeChpbgUM63-l_1rOxnHJfBYPKUTBhFheVGtehnEYJqlc2qAagWOX8j1K7cjZtvQmVxzW5CYsV0FahpT-5ZV695Leh3uieT3_DvA8kzzYAZpXCcKkj2FJKTTErl0SweQC-I1LQPjt9xuk8jqNrtmXuVpSAbRvliz3-RV-cLYfPahJmSeNuSyXo"
        />
        <div className="absolute inset-0 bg-black/40 bg-gradient-to-b from-black/20 to-black/60"></div>
      </div>

      {/* Content Card */}
      <div className="relative z-10 flex w-full max-w-md flex-col items-center justify-center px-6 py-12">
        <div className="w-full flex flex-col items-center bg-white/95 backdrop-blur-md rounded-card p-8 shadow-2xl transition-transform duration-300">
          {/* Icon */}
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-landing-primary shadow-[0_0_15px_rgba(37,244,157,0.4)]">
            <span className="material-symbols-outlined text-4xl text-landing-near-black">
              local_cafe
            </span>
          </div>

          {/* Header */}
          <div className="flex flex-col gap-2 text-center mb-6">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-landing-near-black line-clamp-2">
              {event.title}
            </h1>
            <p className="text-lg font-medium leading-relaxed text-gray-600">
              {event.organizerName} wants to catch up!
            </p>
          </div>

          {/* Event Details */}
          <dl className="w-full space-y-4 mb-8">
            {/* When */}
            <div className="flex flex-col">
              <dt className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
                When
              </dt>
              <dd className="text-base font-medium text-landing-near-black">
                {formatTimeframe(event.timeframe)}
              </dd>
            </div>

            {/* How long */}
            <div className="flex flex-col">
              <dt className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
                How long
              </dt>
              <dd className="text-base font-medium text-landing-near-black">
                {formatDuration(event.duration, event.customDuration)}
              </dd>
            </div>

            {/* Where (only if meetingFormat is defined) */}
            {event.meetingFormat && (
              <div className="flex flex-col">
                <dt className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Where
                </dt>
                <dd className="text-base">{getMeetingFormatText()}</dd>
              </div>
            )}
          </dl>

          {/* CTA Button */}
          <button
            onClick={onContinue}
            className="group relative flex w-full items-center justify-center rounded-full bg-landing-tangerine px-8 py-4 shadow-[0_4px_14px_rgba(255,138,61,0.4)] transition-all hover:bg-[#ff7a2d] active:scale-95 mb-6"
            aria-label="Pick your available times"
          >
            <span className="text-xl font-bold text-white">Choose Availability</span>
            <span className="material-symbols-outlined absolute right-6 text-white/90 group-hover:text-white transition-colors">
              arrow_forward
            </span>
          </button>

          {/* Footer Text */}
          <p className="text-center text-sm font-semibold text-landing-near-black">
            No app or account needed.
          </p>
        </div>
      </div>
    </main>
  );
};
